import { Container } from "@mui/material";

import { Link } from "react-router-dom";

import {
  ActionIcon,
  Badge,
  Button,
  CopyButton,
  Divider,
  Loader,
  Modal,
  Pagination,
  Popover,
  Table,
  Tabs,
  Text,
  TextInput,
  TextInputProps,
  Tooltip,
} from "@mantine/core";
import { useDisclosure } from "@mantine/hooks";
import { useForm } from "@mantine/form";
import {
  IconClipboard,
  IconExternalLink,
  IconEye,
  IconReload,
  IconSearch,
  IconTrash,
} from "@tabler/icons-react";

import { GridRenderCellParams } from "@mui/x-data-grid";
import dayjs from "dayjs";
import { useEffect, useState } from "react";
import DatagridDisplay from "../../Components/DatagridDisplay";
import DisplayAlert from "../../Components/DisplayAlert";
import MegaLoader from "../../Components/MegaLoader";
import {
  BrandImages,
  Colors,
  ValidIDLength,
  ValidMinimumTransactionReferenceLength,
} from "../../Lib/Data";
import { Endpoints } from "../../Lib/Endpoints";
import {
  DefaultErrorNotification,
  DefaultSuccessNotification,
  generateRandomString,
  getFinancialValueFromNumeric,
  getPaginationTotal,
} from "../../Lib/Methods";
import { NonPaginatedResponse, PaginatedResponse } from "../../Lib/Responses";
import {
  OfflinePaymentProps,
  PaginationProps,
  TransactionProps,
  UserProfileProps,
} from "../../Lib/Types";
import { PerformRequest, usePerformRequest } from "../../Lib/usePerformRequest";

import "./styles.scss";

const OfflinePaymentsPage: React.FC = () => {
  const [pagination, setPagination] = useState<PaginationProps>({
    page: 1,
    limit: 20,
  });
  const {
    data: offlinePayments,
    reloadData: reloadOfflinePayments,
    isLoading: isLoadingOfflinePayments,
  } = usePerformRequest<PaginatedResponse<OfflinePaymentProps[]>>({
    method: "GET",
    route: Endpoints.GetOfflinePaymentsPaginated(
      pagination.page,
      pagination.limit
    ),
  });

  const [currentOfflinePayment, setCurrentOfflinePayment] =
    useState<OfflinePaymentProps>();
  const [currentUser, setCurrentUser] = useState<UserProfileProps>();
  const [isLoadingUser, setLoadingUser] = useState<boolean>(false);

  const [
    isTransactionModalOpened,
    { open: OpenTransactionModal, close: CloseTransactionModal },
  ] = useDisclosure(false);

  useEffect(() => {
    const GetUserProfile = async () => {
      setLoadingUser(true);
      if (currentOfflinePayment && currentOfflinePayment.userID) {
        const r = await PerformRequest<NonPaginatedResponse<UserProfileProps>>({
          method: "GET",
          route: Endpoints.GetUserProfile(currentOfflinePayment.userID),
          callback: () => {
            setLoadingUser(false);
          },
        });
        setLoadingUser(false);
        if (r) {
          if (r.status === 404) {
            DefaultErrorNotification("User does not exist!");
          }
          if (r.status === 200) {
            setCurrentUser(r.data.data);
          }
        }
      }
    };
    if (currentOfflinePayment && currentOfflinePayment.userID) {
      GetUserProfile();
    }
  }, [currentOfflinePayment]);

  const [foundTransactions, setFoundTransactions] = useState<
    TransactionProps[]
  >([]);

  const [isSearching, setSearching] = useState<boolean>(false);

  const offlinePaymentIDForm = useForm({
    initialValues: {
      offlineID: "",
    },
    validate: {
      offlineID: (value) =>
        value.length !== ValidIDLength ? "Invalid Offline ID" : null,
    },
  });
  const userIDForm = useForm({
    initialValues: {
      userID: "",
    },
    validate: {
      userID: (value) =>
        value.length !== ValidIDLength ? "Invalid User ID" : null,
    },
  });

  const FilterByTransactionID = async () => {
    const { hasErrors } = offlinePaymentIDForm.validate();
    if (!hasErrors) {
      setSearching(true);
      const { offlineID } = offlinePaymentIDForm.values;
      const r = await PerformRequest<NonPaginatedResponse<TransactionProps>>({
        route: Endpoints.GetSingleOfflinePayment(offlineID),
        callback: () => {
          setSearching(false);
        },
        method: "GET",
      });
      setSearching(false);
      if (r && r.status === 200) {
        setFoundTransactions(r?.data?.data ? [r?.data.data] : []);
      } else {
        DefaultErrorNotification("Could not get transaction!");
      }
    }
  };
  const FilterByUserID = async () => {
    const { hasErrors } = userIDForm.validate();
    if (!hasErrors) {
      setSearching(true);
      const { userID } = userIDForm.values;
      const r = await PerformRequest<NonPaginatedResponse<TransactionProps[]>>({
        route: Endpoints.GetTransactionsByUserID(userID),
        callback: () => {
          setSearching(false);
        },
        method: "GET",
      });
      setSearching(false);
      if (r && r.status === 200) {
        setFoundTransactions(r?.data?.data ?? []);
      } else {
        DefaultErrorNotification("Could not get transactions!");
      }
    }
  };

  const [offlinePaymentLoading, setOfflinePaymentLoading] = useState<
    "approve" | "reject" | null
  >(null);

  const ApproveOfflinePayment = async (
    offlinePaymentID: string,
    paymentType: "new" | "renewal"
  ) => {
    setOfflinePaymentLoading("approve");
    const r = await PerformRequest({
      route: Endpoints.GetUpdateOfflinePaymentStatus(offlinePaymentID),
      method: "PATCH",
      data: {
        status: "approved",
        type: paymentType,
      },
      callback: () => {
        setOfflinePaymentLoading(null);
      },
    });
    setOfflinePaymentLoading(null);
    if (r && r.status === 201) {
      reloadOfflinePayments();
      DefaultSuccessNotification("Offline payment approved!");
      CloseTransactionModal();
    } else {
      DefaultErrorNotification("Could not approve offline payment!");
    }
  };
  const RejectOfflinePayment = async (offlinePaymentID: string) => {
    setOfflinePaymentLoading("reject");
    const r = await PerformRequest({
      route: Endpoints.GetUpdateOfflinePaymentStatus(offlinePaymentID),
      method: "PATCH",
      data: {
        status: "rejected",
        type: "new",
      },
      callback: () => {
        setOfflinePaymentLoading(null);
      },
    });
    setOfflinePaymentLoading(null);
    if (r && r.status === 200) {
      reloadOfflinePayments();
      DefaultSuccessNotification("Offline payment rejected!");
    } else {
      DefaultErrorNotification("Could not reject offline payment!");
    }
  };
  const [isDeletingOfflinePayment, setDeletingOfflinePayment] =
    useState<boolean>(false);
  const DeleteOfflinePayment = async (offlinePaymentID: string) => {
    setDeletingOfflinePayment(true);
    const r = await PerformRequest({
      route: Endpoints.GetDeleteOfflinePayment(offlinePaymentID),
      method: "DELETE",
      callback: () => {
        setDeletingOfflinePayment(false);
      },
    });
    setDeletingOfflinePayment(false);
    if (r && r.status === 200) {
      reloadOfflinePayments();
      DefaultSuccessNotification("Offline payment deleted!");
    } else {
      DefaultErrorNotification("Could not delete offline payment!");
    }
  };
  const filterInputProps: TextInputProps = {
    w: "100%",
    classNames: {
      input: "filter-input",
    },
  };
  return (
    <Container maxWidth="lg">
      <div className="transactions-container">
        <div className="component-header flex-row align-center justify-between">
          <Text className="label">Offline Payments</Text>
          <Button
            onClick={reloadOfflinePayments}
            loading={isLoadingOfflinePayments}
            leftSection={<IconReload size={16} />}
          >
            Reload
          </Button>
        </div>
        <br />
        <Tabs defaultValue="all">
          <Tabs.List grow>
            <Tabs.Tab value="all" leftSection={<IconClipboard size={17} />}>
              All
            </Tabs.Tab>
            <Tabs.Tab value="search" leftSection={<IconSearch size={17} />}>
              Search
            </Tabs.Tab>
          </Tabs.List>

          <Tabs.Panel value="all">
            {offlinePayments && offlinePayments.data && (
              <>
                <DatagridDisplay
                  sx={{
                    marginTop: 3,
                  }}
                  rows={offlinePayments?.data}
                  // onRowClick={(params) => {
                  //   OpenTransactionModal();
                  //   setCurrentOfflinePayment(params.row);
                  // }}
                  isLoading={isLoadingOfflinePayments}
                  columns={[
                    {
                      field: "createdAt",
                      width: 210,
                      headerName: "Date",
                      renderCell: (
                        params: GridRenderCellParams<OfflinePaymentProps>
                      ) => {
                        return (
                          <span>
                            {dayjs(params.row.createdAt).format("DD MMM YYYY")}
                            &nbsp;at&nbsp;
                            {dayjs(params.row.createdAt).format("hh:mmA")}
                          </span>
                        );
                      },
                    },
                    {
                      field: "status",
                      width: 150,

                      headerName: "Status",
                      renderCell: (
                        params: GridRenderCellParams<OfflinePaymentProps>
                      ) => {
                        return (
                          <Badge
                            w={90}
                            color={
                              params.row.status === "approved"
                                ? Colors.GreenPrimary
                                : params.row.status === "rejected"
                                ? Colors.RedPrimary
                                : Colors.OrangePrimary
                            }
                          >
                            {params.row.status}
                          </Badge>
                        );
                      },
                    },
                    {
                      field: "amount",
                      width: 140,

                      headerName: "Amount",
                      renderCell: (
                        params: GridRenderCellParams<OfflinePaymentProps>
                      ) => {
                        return (
                          <span className="amount">
                            ₦
                            {getFinancialValueFromNumeric(
                              params.row.amount / 100
                            )}
                          </span>
                        );
                      },
                    },
                    {
                      field: "type",
                      width: 130,
                      headerName: "Type",
                      renderCell: (
                        params: GridRenderCellParams<OfflinePaymentProps>
                      ) => {
                        return <span>{params.row.type}</span>;
                      },
                    },
                    {
                      field: "duration",

                      headerName: "Duration",
                      renderCell: (
                        params: GridRenderCellParams<OfflinePaymentProps>
                      ) => {
                        return <span>{params.row.duration}</span>;
                      },
                    },
                    {
                      field: "services",
                      width: 130,

                      headerName: "Services",
                      renderCell: (
                        params: GridRenderCellParams<OfflinePaymentProps>
                      ) => {
                        return (
                          <div className="flex-row align-center service-images">
                            {params.row.services.map((service) => {
                              return (
                                <img
                                  src={BrandImages.retrieveImage(service)}
                                  alt=""
                                  className="image"
                                />
                              );
                            })}
                          </div>
                        );
                      },
                    },
                    {
                      field: "actions",
                      width: 150,
                      headerName: "Actions",
                      renderCell: (
                        params: GridRenderCellParams<OfflinePaymentProps>
                      ) => {
                        return (
                          <div className="flex-row align-center flex-1">
                            <ActionIcon
                              variant="outline"
                              color={Colors.BluePrimary}
                              onClick={() => {
                                console.log(params.row);
                                OpenTransactionModal();
                                setCurrentOfflinePayment(params.row);
                              }}
                            >
                              <IconEye size={16} />
                            </ActionIcon>
                            <Popover withArrow>
                              <Popover.Target>
                                <ActionIcon
                                  ml={10}
                                  variant="filled"
                                  color={Colors.RedPrimary}
                                >
                                  <IconTrash size={16} />
                                </ActionIcon>
                              </Popover.Target>
                              <Popover.Dropdown w={280}>
                                <Text fw={500} fz="xs" c={Colors.GraySecondary}>
                                  Are you sure you want to delete this offline
                                  payment?
                                </Text>
                                <Button
                                  mt={10}
                                  fullWidth
                                  loading={isDeletingOfflinePayment}
                                  color={Colors.RedPrimary}
                                  onClick={() => {
                                    DeleteOfflinePayment(params.row.id);
                                  }}
                                >
                                  Delete
                                </Button>
                              </Popover.Dropdown>
                            </Popover>
                          </div>
                        );
                      },
                    },
                  ]}
                />
                {currentOfflinePayment && (
                  <Modal
                    opened={isTransactionModalOpened}
                    onClose={() => {
                      CloseTransactionModal();
                      setCurrentOfflinePayment(undefined);
                      setCurrentUser(undefined);
                    }}
                    title="Offline Payment Details"
                    centered
                    size="lg"
                  >
                    <Table variant="vertical" layout="fixed" withTableBorder>
                      <Table.Thead>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th w={200}>Offline Payment ID</Table.Th>
                          <Table.Td>
                            <CopyButton value={currentOfflinePayment.id}>
                              {({ copied, copy }) => (
                                <Tooltip
                                  label={
                                    copied ? "Copied!" : "Copy to clipboard"
                                  }
                                >
                                  <div
                                    className="pointer flex-row align-center"
                                    onClick={copy}
                                    style={{
                                      color: copied
                                        ? Colors.GreenPrimary
                                        : Colors.BluePrimary,
                                      height: "100%",
                                    }}
                                  >
                                    {currentOfflinePayment.id}
                                  </div>
                                </Tooltip>
                              )}
                            </CopyButton>
                          </Table.Td>
                        </Table.Tr>

                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Date</Table.Th>
                          <Table.Td>
                            {dayjs(currentOfflinePayment.createdAt).format(
                              "DD MMM YYYY - hh:mmA"
                            )}
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Service</Table.Th>
                          <Table.Td>
                            <div className="flex-row align-center temp-service-images">
                              {currentOfflinePayment.services.map((service) => {
                                return (
                                  <img
                                    src={BrandImages.retrieveImage(service)}
                                    alt=""
                                    className="image"
                                  />
                                );
                              })}
                            </div>
                          </Table.Td>
                        </Table.Tr>
                        <Table.Tr key={generateRandomString(12)}>
                          <Table.Th>Amount</Table.Th>
                          <Table.Td fw={600} c={Colors.GreenSecondary}>
                            ₦{currentOfflinePayment.amount / 100}
                          </Table.Td>
                        </Table.Tr>
                        {currentOfflinePayment.iCloudStorage && (
                          <Table.Tr key={generateRandomString(12)}>
                            <Table.Th>iCloud Storage</Table.Th>
                            <Table.Td fw={600} c={Colors.BluePrimary}>
                              {currentOfflinePayment.iCloudStorage}
                            </Table.Td>
                          </Table.Tr>
                        )}
                        {currentOfflinePayment.googleOneStorage && (
                          <Table.Tr key={generateRandomString(12)}>
                            <Table.Th>Google One Storage</Table.Th>
                            <Table.Td fw={600} c={Colors.BluePrimary}>
                              {currentOfflinePayment.googleOneStorage}
                            </Table.Td>
                          </Table.Tr>
                        )}
                        {currentOfflinePayment.coupon && (
                          <Table.Tr key={generateRandomString(12)}>
                            <Table.Th>Coupon Code</Table.Th>
                            <Table.Td>
                              {currentOfflinePayment.coupon.code}
                            </Table.Td>
                          </Table.Tr>
                        )}
                        {currentOfflinePayment.coupon && (
                          <Table.Tr key={generateRandomString(12)}>
                            <Table.Th>Coupon Discount</Table.Th>
                            <Table.Td fw={600} c={Colors.GreenSecondary}>
                              {currentOfflinePayment.coupon.discount}%
                            </Table.Td>
                          </Table.Tr>
                        )}
                      </Table.Thead>
                    </Table>

                    <Divider w="100%" mt={15} mb={10} />
                    <Text fw={500} mb={10} c={Colors.GreenSecondary}>
                      User Details
                    </Text>
                    {currentUser && currentUser && currentUser.id ? (
                      <>
                        <Table
                          mb={20}
                          variant="vertical"
                          layout="fixed"
                          withTableBorder
                        >
                          <Table.Thead>
                            <Table.Tr key={generateRandomString(12)}>
                              <Table.Th w={200}>User ID</Table.Th>
                              <Table.Td>
                                <div className="flex-row align-center">
                                  <CopyButton value={currentUser.id}>
                                    {({ copied, copy }) => (
                                      <Tooltip
                                        label={
                                          copied
                                            ? "Copied!"
                                            : "Copy to clipboard"
                                        }
                                      >
                                        <div
                                          className="pointer flex-row align-center"
                                          onClick={copy}
                                          style={{
                                            color: copied
                                              ? Colors.GreenPrimary
                                              : Colors.BluePrimary,
                                            height: "100%",
                                          }}
                                        >
                                          {currentUser.id}
                                        </div>
                                      </Tooltip>
                                    )}
                                  </CopyButton>
                                  &nbsp; &nbsp;
                                  <Tooltip label="View User Profile">
                                    <Link
                                      to={`/dashboard/user/${currentUser.id}`}
                                    >
                                      <ActionIcon
                                        color={Colors.BluePrimary}
                                        variant="outline"
                                      >
                                        <IconExternalLink size={16} />
                                      </ActionIcon>
                                    </Link>
                                  </Tooltip>
                                </div>
                              </Table.Td>
                            </Table.Tr>
                            <Table.Tr key={generateRandomString(12)}>
                              <Table.Th w={200}>Full Name</Table.Th>
                              <Table.Td>{currentUser.name}</Table.Td>
                            </Table.Tr>
                            <Table.Tr key={generateRandomString(12)}>
                              <Table.Th>Date Joined</Table.Th>
                              <Table.Td>
                                {dayjs(currentUser.createdAt).format(
                                  "DD MMM YYYY - hh:mmA"
                                )}
                              </Table.Td>
                            </Table.Tr>
                            <Table.Tr key={generateRandomString(12)}>
                              <Table.Th>Email</Table.Th>
                              <Table.Td>
                                <CopyButton value={currentUser.email}>
                                  {({ copied, copy }) => (
                                    <Tooltip
                                      label={
                                        copied
                                          ? "Email Copied!"
                                          : "Copy to clipboard"
                                      }
                                    >
                                      <div
                                        className="pointer flex-row align-center"
                                        onClick={copy}
                                        style={{
                                          color: copied
                                            ? Colors.GreenPrimary
                                            : Colors.BluePrimary,
                                          height: "100%",
                                        }}
                                      >
                                        {currentUser.email}
                                      </div>
                                    </Tooltip>
                                  )}
                                </CopyButton>
                              </Table.Td>
                            </Table.Tr>
                            <Table.Tr key={generateRandomString(12)}>
                              <Table.Th>Phone</Table.Th>
                              <Table.Td>
                                <CopyButton value={currentUser.phone}>
                                  {({ copied, copy }) => (
                                    <Tooltip
                                      label={
                                        copied
                                          ? "Phone Copied!"
                                          : "Copy to clipboard"
                                      }
                                    >
                                      <div
                                        className="pointer flex-row align-center"
                                        onClick={copy}
                                        style={{
                                          color: copied
                                            ? Colors.GreenPrimary
                                            : Colors.BluePrimary,
                                          height: "100%",
                                        }}
                                      >
                                        {currentUser.phone}
                                      </div>
                                    </Tooltip>
                                  )}
                                </CopyButton>
                              </Table.Td>
                            </Table.Tr>
                            <Table.Tr key={generateRandomString(12)}>
                              <Table.Th>Promoter</Table.Th>
                              <Table.Td>
                                <Badge
                                  color={
                                    currentUser.isPromoter
                                      ? Colors.GreenPrimary
                                      : Colors.BluePrimary
                                  }
                                >
                                  {currentUser.isPromoter ? "YES" : "NO"}
                                </Badge>
                              </Table.Td>
                            </Table.Tr>
                          </Table.Thead>
                        </Table>
                        {!["approved", "rejected"].includes(
                          currentOfflinePayment.status
                        ) && (
                          <div className="flex-row align-center">
                            <Button
                              loading={offlinePaymentLoading === "approve"}
                              disabled={offlinePaymentLoading === "reject"}
                              color={Colors.GreenPrimary}
                              onClick={() => {
                                if (
                                  currentOfflinePayment &&
                                  currentOfflinePayment.id
                                ) {
                                  ApproveOfflinePayment(
                                    currentOfflinePayment.id,
                                    currentOfflinePayment.type ?? "new"
                                  );
                                }
                              }}
                            >
                              Approve
                            </Button>
                            &nbsp; &nbsp;
                            <Button
                              loading={offlinePaymentLoading === "reject"}
                              disabled={offlinePaymentLoading === "approve"}
                              color={Colors.RedPrimary}
                              onClick={() => {
                                if (
                                  currentOfflinePayment &&
                                  currentOfflinePayment.id
                                ) {
                                  RejectOfflinePayment(
                                    currentOfflinePayment.id
                                  );
                                }
                              }}
                            >
                              Reject
                            </Button>
                          </div>
                        )}
                      </>
                    ) : (
                      <>
                        {isLoadingUser ? (
                          <div className="flex-row align-center justify-center width-100">
                            <Loader my={20} size={34} />
                          </div>
                        ) : (
                          <DisplayAlert
                            title="No User Found"
                            children={
                              <Text>
                                For some reason, this user does not exist :-(
                              </Text>
                            }
                          />
                        )}
                      </>
                    )}
                  </Modal>
                )}
                <div className="flex-row align-center justify-center width-100">
                  <Pagination
                    value={pagination.page}
                    onChange={(value) => {
                      setPagination({ ...pagination, page: value });
                    }}
                    my={40}
                    disabled={
                      offlinePayments?.data.length === 0 ||
                      isLoadingOfflinePayments
                    }
                    total={getPaginationTotal(
                      offlinePayments?.totalRecords,
                      pagination.limit
                    )}
                  />
                </div>
              </>
            )}
            {isLoadingOfflinePayments && (
              <MegaLoader
                containerStyle={{
                  marginTop: "30px",
                }}
              />
            )}
          </Tabs.Panel>
          <Tabs.Panel value="search" className="search-tab">
            <Text mt={20} fw={500} c={Colors.GreenSecondary}>
              Search For Tranasctions
            </Text>
            <div className="flex-row justify-between search-row width-100">
              <div className="flex-col col">
                <TextInput
                  {...offlinePaymentIDForm.getInputProps("offlineID")}
                  {...filterInputProps}
                  spellCheck={false}
                  placeholder={`Enter ${ValidIDLength} character Offline ID`}
                  label="Offline ID"
                />
                <Button
                  w={100}
                  onClick={FilterByTransactionID}
                  loading={isSearching}
                  color={Colors.GreenPrimary}
                >
                  Filter
                </Button>
              </div>
              <div className="flex-col col">
                <TextInput
                  {...userIDForm.getInputProps("userID")}
                  {...filterInputProps}
                  spellCheck={false}
                  placeholder={`Enter ${ValidIDLength} character User ID`}
                  label="User ID"
                />
                <Button
                  w={100}
                  onClick={FilterByUserID}
                  loading={isSearching}
                  color={Colors.GreenPrimary}
                >
                  Filter
                </Button>
              </div>
            </div>
            <Divider w="100%" my={20} />
            {isSearching ? (
              <MegaLoader />
            ) : (
              <>
                {foundTransactions.length > 0 ? (
                  <>
                    <DatagridDisplay
                      sx={{
                        marginTop: 3,
                      }}
                      rows={foundTransactions}
                      onRowClick={(params) => {
                        OpenTransactionModal();
                        setCurrentOfflinePayment(params.row);
                      }}
                      isLoading={isLoadingOfflinePayments}
                      columns={[
                        {
                          field: "createdAt",
                          width: 210,
                          headerName: "Date",
                          renderCell: (
                            params: GridRenderCellParams<OfflinePaymentProps>
                          ) => {
                            return (
                              <span>
                                {dayjs(params.row.createdAt).format(
                                  "DD MMM YYYY"
                                )}
                                &nbsp;at&nbsp;
                                {dayjs(params.row.createdAt).format("hh:mmA")}
                              </span>
                            );
                          },
                        },

                        {
                          field: "amount",
                          width: 140,

                          headerName: "Amount",
                          renderCell: (
                            params: GridRenderCellParams<OfflinePaymentProps>
                          ) => {
                            return (
                              <span className="amount">
                                ₦
                                {getFinancialValueFromNumeric(
                                  params.row.amount / 100
                                )}
                              </span>
                            );
                          },
                        },
                        {
                          field: "duration",

                          headerName: "Duration",
                          renderCell: (
                            params: GridRenderCellParams<OfflinePaymentProps>
                          ) => {
                            return <span>{params.row.duration}</span>;
                          },
                        },
                        {
                          field: "services",
                          width: 210,

                          headerName: "Services",
                          renderCell: (
                            params: GridRenderCellParams<OfflinePaymentProps>
                          ) => {
                            return (
                              <div className="flex-row align-center service-images">
                                {params.row.services.map((service) => {
                                  return (
                                    <img
                                      src={BrandImages.retrieveImage(service)}
                                      alt=""
                                      className="image"
                                    />
                                  );
                                })}
                              </div>
                            );
                          },
                        },
                      ]}
                    />
                  </>
                ) : (
                  <DisplayAlert
                    sx={{
                      marginTop: "30px",
                    }}
                    title="No Transactions Found"
                    children={
                      <Text>
                        No transactions were found for this search. Please try
                        again.
                      </Text>
                    }
                  />
                )}
              </>
            )}
          </Tabs.Panel>
        </Tabs>
      </div>
    </Container>
  );
};
export default OfflinePaymentsPage;
